import React from 'react';
import { TraceCodeDetail } from '../../store/selectors/rawIngredientsSelectors';
import { RawIngredient } from '../../types/rawIngredient';

interface RawIngredientTraceCodeRowProps {
  traceCode: TraceCodeDetail;
  rawIngredient: RawIngredient;
  isEditing: boolean;
  editingName: string;
  editingUnitSize: string;
  editingQuantity: number;
  onStartEdit: (item: RawIngredient) => void;
  onSave: (item: RawIngredient) => void;
  onCancel: () => void;
  onDelete: (id: string, name: string, traceCode: string) => void;
  onNameChange: (value: string) => void;
  onUnitSizeChange: (value: string) => void;
  onQuantityChange: (value: number) => void;
}

export const RawIngredientTraceCodeRow: React.FC<RawIngredientTraceCodeRowProps> = ({
  traceCode,
  rawIngredient,
  isEditing,
  editingName,
  editingUnitSize,
  editingQuantity,
  onStartEdit,
  onSave,
  onCancel,
  onDelete,
  onNameChange,
  onUnitSizeChange,
  onQuantityChange
}) => {
  return (
    <tr
      style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #f0f0f0',
        transition: 'background-color 0.2s'
      }}
      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#fafafa')}
      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
    >
      <td style={{ padding: '12px 16px 12px 48px' }}>
        {isEditing ? (
          <input
            type="text"
            value={editingName}
            onChange={e => onNameChange(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '14px',
              border: '2px solid #4CAF50',
              borderRadius: '4px',
              fontWeight: '500'
            }}
            placeholder="Item name"
          />
        ) : (
          <span
            style={{
              fontWeight: '500',
              color: '#555',
              fontSize: '14px'
            }}
          >
            {rawIngredient.name || '(unnamed)'}
          </span>
        )}
      </td>
      <td style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span
            style={{
              fontWeight: '600',
              color: '#2196F3',
              fontSize: '13px',
              fontFamily: 'monospace'
            }}
          >
            {traceCode.internalTraceCode}
          </span>
          {traceCode.receivingDate && (
            <span
              style={{
                fontSize: '11px',
                color: '#999'
              }}
            >
              Received: {new Date(traceCode.receivingDate).toLocaleDateString()}
            </span>
          )}
        </div>
      </td>
      <td style={{ padding: '12px 16px' }}>
        {isEditing ? (
          <input
            type="text"
            value={editingUnitSize}
            onChange={e => onUnitSizeChange(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '14px',
              border: '2px solid #4CAF50',
              borderRadius: '4px'
            }}
            placeholder="e.g., 5 kg"
          />
        ) : (
          <span
            style={{
              fontWeight: '500',
              color: '#666',
              fontSize: '14px'
            }}
          >
            {rawIngredient.unitSize || '-'}
          </span>
        )}
      </td>
      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
        {isEditing ? (
          <input
            type="number"
            value={editingQuantity}
            onChange={e => onQuantityChange(Number(e.target.value))}
            style={{
              width: '100px',
              padding: '8px',
              fontSize: '14px',
              border: '2px solid #4CAF50',
              borderRadius: '4px',
              textAlign: 'center'
            }}
          />
        ) : (
          <span
            style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#4CAF50'
            }}
          >
            {traceCode.quantity} {traceCode.quantityUnit}
          </span>
        )}
      </td>
      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
        {isEditing ? (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button
              onClick={() => onSave(rawIngredient)}
              style={{
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
            >
              Save
            </button>
            <button
              onClick={onCancel}
              style={{
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button
              onClick={() => onStartEdit(rawIngredient)}
              style={{
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1976D2')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#2196F3')}
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(rawIngredient.id, rawIngredient.name, traceCode.internalTraceCode)}
              style={{
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#d32f2f')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#f44336')}
            >
              Delete
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};
